/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    SEARCH_SPLIT_UTILS
 * Date:         2025-12-30
 * Modified By:  Cursor
 *
 * Description:
 * Extracted URL helper utilities from Search.tsx.
 *
 * Related Screens / Modules:
 * - client/src/components/search/Search.tsx (refactored)
 */

import { useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { SearchState } from "../types";

// [2025-12-30] URL helpers: extracted from Search.tsx
export const useURLHelpers = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const getFilterFromURL = useCallback(
    (paramName: string, defaultValue: string | number = ""): string | number => {
      const searchParams = new URLSearchParams(location.search);
      const value = searchParams.get(paramName);
      if (value === null) return defaultValue;
      // If defaultValue is a number, try to parse the value
      if (typeof defaultValue === "number") {
        const parsed = parseInt(value, 10);
        return isNaN(parsed) ? defaultValue : parsed;
      }
      return value;
    },
    [location.search]
  );

  const updateURLParams = useCallback(
    (updates: Record<string, string | number | null>) => {
      const searchParams = new URLSearchParams(location.search);

      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === "" || value === 0) {
          searchParams.delete(key);
        } else {
          searchParams.set(key, String(value));
        }
      });

      const newSearch = searchParams.toString();
      const newPath = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
      navigate(newPath, { replace: true });
    },
    [location.search, location.pathname, navigate]
  );

  const getURLParamName = useCallback((key: keyof SearchState): string | null => {
    const paramMap: Record<string, string> = {
      q: "q",
      inventoryFilterKeyword: "inv_keyword",
      inventoryPartNumber: "inv_part",
      category: "inv_category",
      condition: "inv_condition",
      manufacturer: "inv_manufacturer",
      country: "inv_country",
      company: "inv_company",
      minQty: "inv_qty",
      inventoryPage: "inv_page",
      inventoryItemsPerPage: "inv_per_page",
      broadcastFilterKeyword: "bc_keyword",
      broadcastSKU: "bc_sku",
      broadcastType: "bc_type",
      broadcastManufacturer: "bc_manufacturer",
      broadcastCategory: "bc_category",
      broadcastQty: "bc_qty",
      broadcastCondition: "bc_condition",
      broadcastCountry: "bc_country",
      broadcastCompany: "bc_company",
      broadcastPage: "bc_page",
      broadcastItemsPerPage: "bc_per_page",
      companyFilterKeyword: "comp_keyword",
      companyCountryFilter: "comp_country",
      companyPage: "comp_page",
      companyItemsPerPage: "comp_per_page",
      sort: "sort",
    };
    return paramMap[key] || null;
  }, []);

  return { getFilterFromURL, updateURLParams, getURLParamName };
};

